import { generateJSON } from '@tiptap/core';
import type { JSONContent } from '@tiptap/react';
import {
  RICH_TEXT_EXTENSIONS,
  toInlineHtmlContent,
  toInlineTextContent,
} from 'components/richText';
import { splitPlaceholderTokens, type PlaceholderSegment } from './placeholderTokens';
import { SELECTION_LIST_PLACEHOLDER, TEXT_MARK_PLACEHOLDER } from './placeholderTypes';

function placeholderNode(segment: Exclude<PlaceholderSegment, { type: 'text' }>): JSONContent {
  return segment.type === 'selectionList'
    ? { type: SELECTION_LIST_PLACEHOLDER, attrs: { flag: segment.flag, name: segment.name } }
    : { type: TEXT_MARK_PLACEHOLDER, attrs: { name: segment.name, value: '' } };
}

function segmentTextNode(node: JSONContent): JSONContent[] {
  if (node.type !== 'text' || !node.text) return [node];
  return splitPlaceholderTokens(node.text).map((segment) =>
    segment.type === 'text' ? { ...node, text: segment.text } : placeholderNode(segment)
  );
}

// Building-block text is trusted, sanitized HTML from the backend (marks like <strong> are intended
// and stray "<" arrives already escaped), so it is parsed as HTML before tokenizing.
export function templateToContent(html: string): JSONContent[] {
  return toInlineHtmlContent(html).flatMap(segmentTextNode);
}

// Selection-list options and nested replacements are plain text (unescaped strings straight from the
// medatixx JSON), so they must NOT go through an HTML parser — "RR <100" would be misparsed as a tag
// and "\n" swallowed. Tokenize, then build explicit text nodes via toInlineTextContent.
export function plainToContent(text: string): JSONContent[] {
  return splitPlaceholderTokens(text).flatMap((segment) =>
    segment.type === 'text' ? toInlineTextContent(segment.text) : [placeholderNode(segment)]
  );
}

// Full doc for setContent on load (paragraph structure preserved). Input is our own saved HTML.
export function toBuildingBlockDoc(html: string): JSONContent {
  const parsed = generateJSON(html || '<p></p>', RICH_TEXT_EXTENSIONS) as JSONContent;
  const paragraphs = parsed.content ?? [];
  return {
    type: 'doc',
    content: paragraphs.map((paragraph) => ({
      ...paragraph,
      content: (paragraph.content ?? []).flatMap(segmentTextNode),
    })),
  };
}
